import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  Plus,
  Trash2,
  Users,
  Eye,
  Link2,
  Link2Off,
  MoreVertical,
  Clock,
  AlertCircle,
  CheckCircle,
  Activity,
} from 'lucide-react';
import { api } from '../../../services/api';
import { PLATFORM_CONFIG, type SMCCPlatformId } from '../../../constants/smcc';

// Simple SVG platform icons
function PlatformIcon({ platform, className = 'w-6 h-6' }: { platform: string; className?: string }) {
  const icons: Record<string, JSX.Element> = {
    instagram: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
    tiktok: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
      </svg>
    ),
    twitter: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    facebook: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    pinterest: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
      </svg>
    ),
    youtube: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
    linkedin: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  };

  return icons[platform] || <Activity className={className} />;
}

interface SMCCAccount {
  id: string;
  platform: string;
  account_name: string;
  account_id: string;
  account_type: 'business' | 'creator' | 'personal';
  profile_image_url?: string;
  follower_count: number;
  following_count: number;
  bio?: string;
  website_url?: string;
  primary_country?: string;
  primary_language?: string;
  status: 'active' | 'disconnected' | 'expired' | 'suspended' | 'pending';
  last_sync_at?: string;
  last_post_at?: string;
  sync_error?: string;
  created_at: string;
  updated_at: string;
}

interface SMCCPlatformInfo {
  platform: string;
  configured: boolean;
  scopes: string[];
}

function AccountStatusBadge({ status }: { status: SMCCAccount['status'] }) {
  const config = {
    active: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Active' },
    disconnected: { color: 'bg-gray-100 text-gray-700', icon: Link2Off, label: 'Disconnected' },
    expired: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Token Expired' },
    suspended: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Suspended' },
    pending: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Pending' },
  };

  const { color, icon: Icon, label } = config[status] || config.disconnected;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function AccountCard({
  account,
  onRefresh,
  onSync,
  onDisconnect,
  isRefreshing,
  isSyncing,
}: {
  account: SMCCAccount;
  onRefresh: () => void;
  onSync: () => void;
  onDisconnect: () => void;
  isRefreshing: boolean;
  isSyncing: boolean;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const platformConfig = PLATFORM_CONFIG[account.platform as SMCCPlatformId] || {
    id: account.platform as SMCCPlatformId,
    color: '#666',
    bgColor: 'bg-gray-50',
    name: account.platform,
    maxLength: 2200,
    maxHashtags: 30,
    supportsMedia: true,
    maxImages: 10,
    maxVideos: 1,
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Profile Image */}
          <div className="relative">
            {account.profile_image_url ? (
              <img
                src={account.profile_image_url}
                alt={account.account_name}
                className="w-14 h-14 rounded-full object-cover"
              />
            ) : (
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center ${platformConfig.bgColor}`}
                style={{ color: platformConfig.color }}
              >
                <PlatformIcon platform={account.platform} className="w-7 h-7" />
              </div>
            )}
            {/* Platform badge */}
            <div
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center bg-white shadow-sm border border-gray-200"
              style={{ color: platformConfig.color }}
            >
              <PlatformIcon platform={account.platform} className="w-4 h-4" />
            </div>
          </div>

          {/* Account Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 truncate">@{account.account_name}</h3>
              <AccountStatusBadge status={account.status} />
            </div>
            <p className="text-sm text-gray-500">{platformConfig.name}</p>
            <p className="text-xs text-gray-400 capitalize">{account.account_type} account</p>
          </div>

          {/* Menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                  onKeyDown={(e) => e.key === 'Escape' && setShowMenu(false)}
                  role="button"
                  tabIndex={0}
                  aria-label="Close menu"
                />
                <div className="absolute right-0 top-10 z-20 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  <button
                    onClick={() => {
                      onRefresh();
                      setShowMenu(false);
                    }}
                    disabled={isRefreshing || account.status === 'disconnected'}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh Token
                  </button>
                  <button
                    onClick={() => {
                      onSync();
                      setShowMenu(false);
                    }}
                    disabled={isSyncing || account.status !== 'active'}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Activity className={`w-4 h-4 ${isSyncing ? 'animate-pulse' : ''}`} />
                    Sync Stats
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      if (confirm(`Disconnect @${account.account_name}?`)) {
                        onDisconnect();
                      }
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Disconnect
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 flex items-center gap-6">
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="font-medium text-gray-900">
              {account.follower_count.toLocaleString()}
            </span>
            <span className="text-gray-500">followers</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Eye className="w-4 h-4 text-gray-400" />
            <span className="font-medium text-gray-900">
              {account.following_count.toLocaleString()}
            </span>
            <span className="text-gray-500">following</span>
          </div>
        </div>

        {/* Error Message */}
        {account.sync_error && (
          <div className="mt-3 p-2 bg-red-50 rounded-lg text-sm text-red-600 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{account.sync_error}</span>
          </div>
        )}

        {/* Last Sync */}
        {account.last_sync_at && (
          <div className="mt-3 text-xs text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Last synced: {new Date(account.last_sync_at).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectPlatformCard({
  platform,
  connectedCount,
  onConnect,
  isConnecting,
}: {
  platform: SMCCPlatformInfo;
  connectedCount: number;
  onConnect: () => void;
  isConnecting: boolean;
}) {
  const config = PLATFORM_CONFIG[platform.platform as SMCCPlatformId] || {
    id: platform.platform as SMCCPlatformId,
    color: '#666',
    bgColor: 'bg-gray-50',
    name: platform.platform,
    maxLength: 2200,
    maxHashtags: 30,
    supportsMedia: true,
    maxImages: 10,
    maxVideos: 1,
  };

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 ${
        !platform.configured ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.bgColor}`}
          style={{ color: config.color }}
        >
          <PlatformIcon platform={platform.platform} className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">{config.name}</h3>
          {connectedCount > 0 && (
            <p className="text-xs text-gray-500">
              {connectedCount} account{connectedCount !== 1 ? 's' : ''} connected
            </p>
          )}
        </div>
        {platform.configured ? (
          <button
            onClick={onConnect}
            disabled={isConnecting}
            className="px-3 py-1.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Connect
          </button>
        ) : (
          <span className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-lg">
            Not Configured
          </span>
        )}
      </div>
    </div>
  );
}

export default function SMCCAccounts() {
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [refreshingAccount, setRefreshingAccount] = useState<string | null>(null);
  const [syncingAccount, setSyncingAccount] = useState<string | null>(null);

  // Fetch platforms
  const { data: platformsData, isLoading: loadingPlatforms } = useQuery({
    queryKey: ['smcc-platforms'],
    queryFn: () => api.getSMCCPlatforms(),
  });

  // Fetch accounts
  const { data: accountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ['smcc-accounts'],
    queryFn: () => api.getSMCCAccounts(),
    refetchInterval: 30000,
  });

  // Connect account mutation
  const connectMutation = useMutation({
    mutationFn: async (platform: string) => {
      const result = await api.connectSMCCAccount(platform, `${window.location.origin}/admin/smcc/callback`);
      // Redirect to OAuth URL
      if (result.auth_url) {
        window.location.href = result.auth_url;
      }
      return result;
    },
    onError: (error: Error) => {
      setConnectingPlatform(null);
      showError(error.message || 'Failed to initiate connection');
    },
  });

  // Refresh token mutation with optimistic update
  const refreshMutation = useMutation({
    mutationFn: (accountId: string) => api.refreshSMCCAccount(accountId),
    onMutate: async (accountId: string) => {
      await queryClient.cancelQueries({ queryKey: ['smcc-accounts'] });
      const previous = queryClient.getQueryData(['smcc-accounts']);
      // Optimistically show "pending" status while refreshing
      queryClient.setQueryData(['smcc-accounts'], (old: { accounts: SMCCAccount[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          accounts: old.accounts.map((acc) =>
            acc.id === accountId ? { ...acc, status: 'pending' as const, sync_error: undefined } : acc
          ),
        };
      });
      return { previous };
    },
    onSuccess: () => {
      setRefreshingAccount(null);
      showSuccess('Token refreshed successfully');
    },
    onError: (error: Error, _accountId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['smcc-accounts'], context.previous);
      }
      setRefreshingAccount(null);
      showError(error.message || 'Failed to refresh token');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-accounts'] });
    },
  });

  // Sync account mutation with optimistic update
  const syncMutation = useMutation({
    mutationFn: (accountId: string) => api.syncSMCCAccount(accountId),
    onMutate: async (accountId: string) => {
      await queryClient.cancelQueries({ queryKey: ['smcc-accounts'] });
      const previous = queryClient.getQueryData(['smcc-accounts']);
      // Optimistically update last_sync_at to now
      queryClient.setQueryData(['smcc-accounts'], (old: { accounts: SMCCAccount[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          accounts: old.accounts.map((acc) =>
            acc.id === accountId ? { ...acc, last_sync_at: new Date().toISOString() } : acc
          ),
        };
      });
      return { previous };
    },
    onSuccess: () => {
      setSyncingAccount(null);
      showSuccess('Account synced successfully');
    },
    onError: (error: Error, _accountId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['smcc-accounts'], context.previous);
      }
      setSyncingAccount(null);
      showError(error.message || 'Failed to sync account');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-accounts'] });
    },
  });

  // Disconnect account mutation with optimistic update
  const disconnectMutation = useMutation({
    mutationFn: (accountId: string) => api.disconnectSMCCAccount(accountId),
    onMutate: async (accountId: string) => {
      await queryClient.cancelQueries({ queryKey: ['smcc-accounts'] });
      const previous = queryClient.getQueryData(['smcc-accounts']);
      // Optimistically remove from list
      queryClient.setQueryData(['smcc-accounts'], (old: { accounts: SMCCAccount[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          accounts: old.accounts.filter((acc) => acc.id !== accountId),
        };
      });
      return { previous };
    },
    onSuccess: () => {
      showSuccess('Account disconnected');
    },
    onError: (error: Error, _accountId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['smcc-accounts'], context.previous);
      }
      showError(error.message || 'Failed to disconnect account');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['smcc-platforms'] });
    },
  });

  // Sync all mutation
  const syncAllMutation = useMutation({
    mutationFn: () => api.syncAllSMCCAccounts(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['smcc-accounts'] });
      showSuccess(`Synced ${result.successful} accounts`);
    },
    onError: (error: Error) => showError(error.message || 'Failed to sync accounts'),
  });

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setErrorMessage('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (msg: string) => {
    setErrorMessage(msg);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  };

  const handleConnect = (platform: string) => {
    setConnectingPlatform(platform);
    connectMutation.mutate(platform);
  };

  const handleRefresh = (accountId: string) => {
    setRefreshingAccount(accountId);
    refreshMutation.mutate(accountId);
  };

  const handleSync = (accountId: string) => {
    setSyncingAccount(accountId);
    syncMutation.mutate(accountId);
  };

  const platforms = (platformsData as { platforms: SMCCPlatformInfo[]; total: number } | undefined)?.platforms || [];
  const accounts = (accountsData as { accounts: SMCCAccount[] } | undefined)?.accounts || [];

  // Count connected accounts per platform
  const connectedCountByPlatform = accounts.reduce((acc, account) => {
    acc[account.platform] = (acc[account.platform] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const activeAccounts = accounts.filter((a) => a.status === 'active');
  const problemAccounts = accounts.filter((a) => ['disconnected', 'expired', 'suspended'].includes(a.status));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Media Accounts</h1>
          <p className="text-gray-500 mt-1">
            Connect and manage your social media accounts for the SMCC
          </p>
        </div>
        {accounts.length > 0 && (
          <button
            onClick={() => syncAllMutation.mutate()}
            disabled={syncAllMutation.isPending}
            className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${syncAllMutation.isPending ? 'animate-spin' : ''}`} />
            Sync All
          </button>
        )}
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <Check className="w-5 h-5" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {errorMessage}
        </div>
      )}

      {/* Stats Summary */}
      {accounts.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Link2 className="w-4 h-4" />
              <span className="text-sm">Connected</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{activeAccounts.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Needs Attention</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{problemAccounts.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total Followers</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {accounts.reduce((sum, a) => sum + a.follower_count, 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Activity className="w-4 h-4" />
              <span className="text-sm">Platforms</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {new Set(accounts.map((a) => a.platform)).size}
            </p>
          </div>
        </div>
      )}

      {/* Problem Accounts Alert */}
      {problemAccounts.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">
                {problemAccounts.length} account{problemAccounts.length !== 1 ? 's' : ''} need attention
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Some accounts have disconnected or expired tokens. Reconnect them to continue posting.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Connect New Platform */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Connect Platform</h2>
        {loadingPlatforms ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-24 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {platforms.map((platform) => (
              <ConnectPlatformCard
                key={platform.platform}
                platform={platform}
                connectedCount={connectedCountByPlatform[platform.platform] || 0}
                onConnect={() => handleConnect(platform.platform)}
                isConnecting={connectingPlatform === platform.platform}
              />
            ))}
          </div>
        )}
      </div>

      {/* Connected Accounts */}
      {accounts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Connected Accounts</h2>
          {loadingAccounts ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gray-200 rounded-full" />
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 rounded w-32 mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-20" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {accounts.map((account) => (
                <AccountCard
                  key={account.id}
                  account={account}
                  onRefresh={() => handleRefresh(account.id)}
                  onSync={() => handleSync(account.id)}
                  onDisconnect={() => disconnectMutation.mutate(account.id)}
                  isRefreshing={refreshingAccount === account.id}
                  isSyncing={syncingAccount === account.id}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loadingAccounts && accounts.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Link2 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No accounts connected</h3>
          <p className="text-gray-500 mb-4">
            Connect your social media accounts to start managing your content from SMCC.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <ExternalLink className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">About Account Connections</p>
            <p className="text-sm text-blue-700 mt-1">
              OAuth tokens are securely encrypted and stored. Tokens are automatically refreshed before expiry.
              If a token expires, you'll need to reconnect the account to continue posting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
